/**
 * stoneTokens.js — The Stone Layer
 *
 * TogeeVision Slicing Protocol v1.0
 *
 * The rhythm unit (R) is not arbitrary. It is derived from the LightRampSwirl:
 *   STRIPE (9px) = the visible light — the signal passing through
 *   GAP    (7px) = the dark interval — the load-bearing rest
 *   R     (16px) = one complete breath cycle
 *
 * All spacing, typography, and layout in the app should breathe at multiples of R.
 * The Stone does not fight the signal — it gives it somewhere to land.
 *
 * B_e = ∫ Φ_Illumination / (Σ_Stone − Δ_Mechanical) dt
 */

// ─── The Interval ────────────────────────────────────────────────────────────

export const STRIPE = 9;        // visible slice — the light
export const GAP = 7;           // dark rest — the stone
export const R = STRIPE + GAP;  // 16px — base rhythm unit

// Rhythm scale (multiples of R)
export const R0 = 0;
export const R_HALF = R / 2;    // 8px  — micro spacing
export const R1 = R;            // 16px — base unit
export const R1_5 = R * 1.5;    // 24px — intermediate rhythm
export const R2 = R * 2;        // 32px — section breathing room
export const R3 = R * 3;        // 48px — section separation
export const R4 = R * 4;        // 64px — major intervals
export const R5 = R * 5;        // 80px
export const R05 = R * 0.5;     // 8px  — alias for R_HALF
export const R6 = R * 6;        // 96px — hero padding
export const R8 = R * 8;        // 128px — structural separation
export const R10 = R * 10;      // 160px
export const R15 = R * 15;      // 240px — large section gutters

// ─── Palette ─────────────────────────────────────────────────────────────────

// The Sun — primary signal accent
export const SIGNAL_ORANGE = '#e07830';
export const SIGNAL_ORANGE_DIM = 'rgba(224, 120, 48, 0.55)';
export const SIGNAL_ORANGE_GHOST = 'rgba(224, 120, 48, 0.12)';
export const SIGNAL_ORANGE_BORDER = 'rgba(224, 120, 48, 0.28)';

// The Signal — secondary creative/tool accent (canvas, collab, palettes)
export const SIGNAL_BLUE = '#38bdf8';
export const SIGNAL_BLUE_DIM = 'rgba(56, 189, 248, 0.55)';
export const SIGNAL_BLUE_GHOST = 'rgba(56, 189, 248, 0.12)';
export const SIGNAL_BLUE_BORDER = 'rgba(56, 189, 248, 0.28)';

// The Grove — status/confirmation accent
export const SIGNAL_GREEN = '#34d399';
export const SIGNAL_CYAN = '#22d3ee';

// The Field — background surfaces
export const FIELD_BLACK = '#111111';      // hero + full-bleed sections
export const DEEP_BLACK = '#0c0c0c';       // deepest — footer, drawers

// Surface Container Tiers (No-Line Spatial Logic)
export const SURFACE_LOWEST = '#0c0e13';
export const SURFACE_LOW = '#1a1b21';
export const SURFACE_CONTAINER = '#1e1f25';
export const SURFACE_HIGH = '#282a2f';
export const SURFACE_HIGHEST = '#33353a';

export const PAPER_DARK = '#1a1a1f';       // elevated surfaces / cards
export const PAPER_MID = '#1f1f26';        // modals, popovers

// The Swirl — stroke tones
export const SWIRL_WHITE = 'rgba(255, 255, 255, 0.85)';
export const SWIRL_WHITE_DIM = 'rgba(255, 255, 255, 0.4)';

// Text
export const TEXT_PRIMARY = '#e2e1ee';
export const TEXT_SECONDARY = 'rgba(226, 225, 238, 0.65)';
export const TEXT_MUTED = 'rgba(226, 225, 238, 0.38)';

// ─── Opacity ramp ────────────────────────────────────────────────────────────

// Use these for layered overlays so every element follows the same opacity grammar
export const RAMP_GHOST = 0.08;
export const RAMP_LOW = 0.18;
export const RAMP_MID = 0.42;
export const RAMP_HIGH = 0.78;
export const RAMP_FULL = 1;

// ─── Typography rhythm ───────────────────────────────────────────────────────

// Line heights derived from R so text sits naturally in the grid
export const LH_TIGHT = 1.0;    // display / hero headlines
export const LH_SNUG = 1.25;    // subheadings
export const LH_BASE = 1.5;     // body — exactly 1.5 × R when font-size = R
export const LH_LOOSE = 1.75;   // long-form reading

// Letter spacing
export const LS_TIGHT = '-0.04em';
export const LS_NORMAL = '0em';
export const LS_WIDE = '0.08em';
export const LS_CAPS = '0.12em';

// ─── Borders & radius ────────────────────────────────────────────────────────

export const RADIUS_MD = { xs: '8px', md: '12px' }; // 12px — cards (8px on mobile)
export const RADIUS_LG = { xs: '12px', md: '20px' }; // 20px — panels (12px on mobile)
export const RADIUS_PILL = 999;    // fully rounded

export const BORDER_SIGNAL = `1px solid ${SIGNAL_ORANGE_BORDER}`;
export const BORDER_SUBTLE = '1px solid rgba(148, 163, 184, 0.12)';
export const BORDER_GHOST = '1px solid rgba(224, 120, 48, 0.08)';

export const GLASS_BACKDROP = 'blur(16px)';
export const GLASS_BG = 'rgba(17, 19, 24, 0.7)';

// ─── Shadows ─────────────────────────────────────────────────────────────────

export const SHADOW_CARD = `0 ${R}px ${R3}px -${R}px rgba(224, 120, 48, 0.18)`;
export const SHADOW_CARD_HOVER = `0 ${R2}px ${R4}px -${R}px rgba(224, 120, 48, 0.32)`;
export const SHADOW_GLOW = `0 0 ${R2}px rgba(224, 120, 48, 0.25)`;

// ─── Z-index scale ───────────────────────────────────────────────────────────

export const Z_SWIRL = 0;       // background geometric layer
export const Z_CONTENT = 1;     // foreground content
export const Z_OVERLAY = 10;    // overlays / modals
export const Z_PLAYER = 1201;   // mini player (above MUI AppBar at 1200)
